import { Injectable } from '@angular/core';

import { SignalingConfigService } from './signaling-config.service';
import { SignalingTokenService } from './signaling-token.service';

/**
 * Debug helper for testing signaling service connection.
 * Expose this to window for easy browser console testing.
 * Usage in browser console:
 *   window.signalingDebug.setEndpoint('https://...')
 *   window.signalingDebug.setTokenEndpoint('/api/v1/signaling/signal-token')
 *   window.signalingDebug.testConnection()
 */
interface SignalingDebugApi {
  setEndpoint: (endpoint: string) => void;
  setTokenEndpoint: (endpoint: string) => void;
  testConnection: (quoteId?: string) => Promise<void>;
  closeTest: () => void;
  getStatus: () => unknown;
  requestToken: (peerId?: string, quoteId?: string) => Promise<string | null>;
}

interface SignalingDebugStatus {
  signalingEndpointConfigured: boolean;
  tokenEndpointConfigured: boolean;
  signalingEndpoint: string;
  tokenEndpoint: string;
  wsConnected: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class SignalingDebugService {
  private testWebSocket: WebSocket | null = null;

  constructor(
    private readonly signalingConfig: SignalingConfigService,
    private readonly signalingToken: SignalingTokenService
  ) {
    this.exposeToWindow();
  }

  /**
   * Expose debug methods to window object for console access.
   */
  private exposeToWindow(): void {
    const debug: SignalingDebugApi = {
      setEndpoint: (endpoint: string) => this.setEndpoint(endpoint),
      setTokenEndpoint: (endpoint: string) => this.setTokenEndpoint(endpoint),
      testConnection: (quoteId?: string) => this.testConnection(quoteId),
      closeTest: () => this.closeTestConnection(),
      getStatus: () => this.getStatus(),
      requestToken: (peerId?: string, quoteId?: string) =>
        this.requestTestToken(peerId ?? `debug-${Date.now()}`, quoteId)
    };

    (window as unknown as { signalingDebug?: SignalingDebugApi }).signalingDebug = debug;
    console.log('[SignalingDebug] Debug API exposed to window.signalingDebug');
  }

  /**
   * Set signaling endpoint for testing.
   */
  setEndpoint(endpoint: string): void {
    this.signalingConfig.setEndpoint(endpoint);
    console.log(`[SignalingDebug] Endpoint set to: ${endpoint}`);
  }

  /**
   * Set token endpoint for testing.
   */
  setTokenEndpoint(endpoint: string): void {
    this.signalingConfig.setTokenEndpoint(endpoint);
    console.log(`[SignalingDebug] Token endpoint set to: ${endpoint}`);
  }

  /**
   * Request a test token from backend.
   */
  async requestTestToken(peerId: string = 'debug-peer', quoteId?: string): Promise<string | null> {
    const token = await this.signalingToken.getToken(peerId, quoteId);
    if (!token) {
      console.warn('[SignalingDebug] Token request failed');

      return null;
    }

    console.log(`[SignalingDebug] Token: ${token}`);

    return token;
  }

  /**
   * Test WebSocket connection to signaling service.
   */
  async testConnection(quoteId?: string): Promise<void> {
    const endpoint = this.signalingConfig.getEndpoint();
    const wsUrl = this.signalingConfig.getWebSocketUrl();

    console.log('[SignalingDebug] Testing connection...');
    console.log(`  Endpoint: ${endpoint}`);
    console.log(`  WebSocket URL: ${wsUrl}`);

    if (!endpoint || !wsUrl) {
      console.error('[SignalingDebug] Signaling endpoint not configured');

      return;
    }

    if (this.testWebSocket) {
      this.closeTestConnection();
    }

    const peerId = `debug-${Date.now()}`;
    const token = await this.signalingToken.getToken(peerId, quoteId);
    if (!token) {
      console.error('[SignalingDebug] Unable to obtain backend-issued signaling token');

      return;
    }

    const wsUrlWithAuth = `${wsUrl}?token=${encodeURIComponent(token)}&peerId=${encodeURIComponent(peerId)}`;
    console.log(`[SignalingDebug] Connecting as peer: ${peerId}`);

    this.testWebSocket = new WebSocket(wsUrlWithAuth);

    this.testWebSocket.onopen = () => {
      console.log('[SignalingDebug] ✓ WebSocket connected!');
      // Send a ping
      const socket = this.testWebSocket;
      if (!socket) {
        return;
      }

      socket.send(
        JSON.stringify({
          type: 'PING',
          payload: {}
        })
      );
    };

    this.testWebSocket.onmessage = (event) => {
      console.log('[SignalingDebug] Message received:', event.data);
    };

    this.testWebSocket.onerror = (error) => {
      console.error('[SignalingDebug] ✗ WebSocket error:', error);
    };

    this.testWebSocket.onclose = () => {
      console.log('[SignalingDebug] WebSocket closed');
      this.testWebSocket = null;
    };
  }

  /**
   * Close test connection.
   */
  closeTestConnection(): void {
    if (this.testWebSocket) {
      this.testWebSocket.close();
      this.testWebSocket = null;
      console.log('[SignalingDebug] Test connection closed');
    }
  }

  /**
   * Get current debug status.
   */
  getStatus(): SignalingDebugStatus {
    return {
      ...this.signalingConfig.getReadiness(),
      wsConnected: this.testWebSocket ? this.testWebSocket.readyState === WebSocket.OPEN : false
    };
  }
}
